import { makeAutoObservable, reaction, runInAction } from "mobx";
import { isSessionEnded } from "./sessionGuard";

/**
 * Notification entity returned by the `notifications` service.
 */
interface AppNotification {
  id: number;
  isRead?: boolean;
  /** Target user id — null/undefined means broadcast to all staff. */
  userId?: number | null;
  [key: string]: unknown;
}

/**
 * Minimal structural view of a Feathers service as exposed by the shared
 * apiClient. Avoids importing Feathers types into this store.
 */
interface FeathersServiceLike {
  find(params?: unknown): Promise<unknown>;
  create(data: unknown, params?: unknown): Promise<unknown>;
  on(event: string, handler: (data: AppNotification) => void): void;
  removeListener(event: string, handler: (data: AppNotification) => void): void;
}

/**
 * Minimal structural view of the RootStore that this store depends on.
 * Avoids a hard type-coupling (and the resulting circular import) while
 * still removing the `any`.
 */
interface NotificationRootStore {
  authStore?: {
    isSignedIn?: boolean;
    currentUser?: { id: number | string } | null;
  };
  app?: {
    service(path: string): FeathersServiceLike;
  } | null;
  /** Snackbar/toast store (library NotificationStore). */
  notificationStore?: {
    addNotification(notification: {
      message: string;
      options?: { variant?: string; autoHideDuration?: number };
    }): void;
  };
  /**
   * Secondary realtime socket (stores/RealtimeStore.ts). Used as a fallback
   * event source — when it receives a `notifications` event we refetch, in
   * case the main Feathers socket missed it.
   */
  realtimeStore?: {
    connect(): Promise<void> | void;
    lastEvent: { service: string; action: string; at: number } | null;
  };
}

const PAGE_SIZE = 50;

/**
 * API-backed notification store. Powers the bell icon in the toolbar:
 * loads notifications for the currently signed-in user, tracks unread
 * count, and exposes mark-as-read actions (via the `notifications-actions`
 * service: `mark-read` / `mark-all-read`).
 *
 * Realtime: the API publishes each notification `created` event only to the
 * target user's own `user/<id>` channel (broadcast rows with userId NULL
 * arrive via the `internal` channel), so this store can listen on the
 * authenticated Feathers socket without any client-side userId filtering.
 * Polling remains as a fallback for missed events / reconnects.
 *
 * The in-app toast/snackbar store lives separately in `NotificationStore.ts`.
 */
class ApiNotificationStore {
  rootStore: NotificationRootStore;
  notifications: AppNotification[] = [];
  unreadCount = 0;
  loading = false;
  pollInterval: ReturnType<typeof setInterval> | null = null;
  /**
   * Monotonic guard against out-of-order responses (mirrors
   * CommandCenterStore.loadSeq): the 60s poll, the realtime fallback and every
   * bell open all call loadNotifications, so two finds can be in flight at
   * once. Only the latest one may apply, and mark-read mutations bump the seq
   * so a stale pre-mutation snapshot can't revert the optimistic flip.
   */
  private loadSeq = 0;
  private listenersAttached = false;
  private realtimeFallbackDisposer: (() => void) | null = null;

  constructor(rootStore: NotificationRootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      rootStore: false,
      pollInterval: false,
    });

    // Auto start/stop polling + realtime listeners based on auth state
    reaction(
      () => Boolean(rootStore?.authStore?.isSignedIn),
      (isSignedIn: boolean) => {
        if (isSignedIn) {
          this.startPolling();
        } else {
          this.stopPolling();
        }
      },
      { fireImmediately: true }
    );
  }

  private get notificationsService(): FeathersServiceLike | null {
    try {
      return this.rootStore?.app?.service("notifications") ?? null;
    } catch {
      return null;
    }
  }

  private get actionsService(): FeathersServiceLike | null {
    try {
      return this.rootStore?.app?.service("notifications-actions") ?? null;
    } catch {
      return null;
    }
  }

  private recalcUnread() {
    // Broadcast rows (userId null) have no per-user read state on the server
    // (mark-read is scoped to the caller's OWN rows), so they are informational
    // only and never count toward the badge — matching dashboard-summary's
    // unreadForCaller, which also excludes broadcasts.
    this.unreadCount = this.notifications.filter(
      (n) => !n.isRead && n.userId != null
    ).length;
  }

  async loadNotifications() {
    const seq = ++this.loadSeq;
    const service = this.notificationsService;
    if (!service || !this.rootStore?.authStore?.isSignedIn) {
      runInAction(() => {
        this.notifications = [];
        this.unreadCount = 0;
        this.loading = false;
      });
      return;
    }

    runInAction(() => {
      this.loading = true;
    });
    try {
      // The API scopes find() to the caller's own rows (+ broadcasts).
      const result = (await service.find({
        query: { $sort: { createdAt: -1 }, $limit: PAGE_SIZE },
      })) as { data?: AppNotification[] } | AppNotification[];
      // Discard stale responses — a newer load (or a mark-read mutation that
      // bumped loadSeq) supersedes this snapshot.
      if (seq !== this.loadSeq) return;
      const rows = Array.isArray(result) ? result : result?.data || [];
      runInAction(() => {
        this.notifications = rows;
        this.recalcUnread();
        this.loading = false;
      });
    } catch (err) {
      console.error("Failed to load notifications:", err);
      if (seq !== this.loadSeq) return;
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async markAsRead(id: number) {
    const actions = this.actionsService;
    if (!actions) return;
    const row = this.notifications.find((n) => n.id === id);
    // Broadcast rows (userId null) have no per-user read state — the server's
    // mark-read is scoped to the caller's own rows and would update 0, so an
    // optimistic flip would only be reverted by the next poll. Leave them.
    if (!row || row.userId == null) return;
    const wasRead = row.isRead ?? false;
    const rollback = () => {
      runInAction(() => {
        row.isRead = wasRead;
        this.recalcUnread();
      });
    };
    // Optimistic: flip immediately, roll back on failure.
    runInAction(() => {
      row.isRead = true;
      this.recalcUnread();
    });
    // Invalidate any in-flight find that predates this mutation so its stale
    // (isRead=false) snapshot cannot land after the write commits.
    this.loadSeq++;
    try {
      const result = (await actions.create({
        action: "mark-read",
        ids: [id],
      })) as { updated?: number } | undefined;
      // updated === 0 means the server matched no owned row (e.g. it was
      // removed or reassigned) — the flip never persisted, so undo it.
      if (result?.updated === 0) rollback();
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
      // The sign-out ended it: the row is the previous person's, the purge has
      // taken it, and the badge is not this call's to recount any more.
      if (isSessionEnded(err)) return;
      rollback();
    }
  }

  async markAllAsRead() {
    const actions = this.actionsService;
    if (!actions) return;
    // Optimistic: flip immediately, roll back on failure. Broadcast rows
    // (userId null) are skipped — the server's mark-all-read only updates the
    // caller's OWN rows, so flipping broadcasts would just revert on refetch.
    const previouslyUnreadIds = this.notifications
      .filter((n) => !n.isRead && n.userId != null)
      .map((n) => n.id);
    runInAction(() => {
      this.notifications.forEach((n) => {
        if (n.userId != null) n.isRead = true;
      });
      this.unreadCount = 0;
    });
    // Invalidate any in-flight find that predates this mutation.
    this.loadSeq++;
    try {
      await actions.create({ action: "mark-all-read" });
    } catch (err) {
      console.error("Failed to mark all notifications as read:", err);
      // The sign-out ended it: the rows the rollback would touch are the
      // previous person's, and the purge has taken them away.
      if (isSessionEnded(err)) return;
      runInAction(() => {
        this.notifications.forEach((n) => {
          if (previouslyUnreadIds.includes(n.id)) n.isRead = false;
        });
        this.recalcUnread();
      });
    }
  }

  // ─── Realtime listeners ────────────────────────────────────────────────

  private handleCreated = (data: AppNotification) => {
    if (!data || typeof data.id !== "number") return;
    let isNew = false;
    runInAction(() => {
      if (this.notifications.some((n) => n.id === data.id)) return;
      isNew = true;
      this.notifications = [data, ...this.notifications].slice(0, PAGE_SIZE);
      this.recalcUnread();
    });
    if (!isNew) return;
    // Surface high-priority notifications as a snackbar toast as well.
    const priority = typeof data.priority === "string" ? data.priority : "";
    if (priority === "high" || priority === "urgent") {
      const title = typeof data.title === "string" ? data.title : "Notification";
      this.rootStore?.notificationStore?.addNotification?.({
        message: title,
        options: {
          variant: priority === "urgent" ? "error" : "warning",
          autoHideDuration: 6000,
        },
      });
    }
  };

  private handlePatched = (data: AppNotification) => {
    if (!data || typeof data.id !== "number") return;
    runInAction(() => {
      const idx = this.notifications.findIndex((n) => n.id === data.id);
      if (idx >= 0) {
        this.notifications[idx] = { ...this.notifications[idx], ...data };
        this.recalcUnread();
      }
    });
  };

  private attachListeners() {
    if (this.listenersAttached) return;
    const service = this.notificationsService;
    if (!service || typeof service.on !== "function") return;
    service.on("created", this.handleCreated);
    service.on("patched", this.handlePatched);
    this.listenersAttached = true;
  }

  private detachListeners() {
    if (!this.listenersAttached) return;
    const service = this.notificationsService;
    if (service && typeof service.removeListener === "function") {
      service.removeListener("created", this.handleCreated);
      service.removeListener("patched", this.handlePatched);
    }
    this.listenersAttached = false;
  }

  /**
   * Fallback realtime path: the standalone RealtimeStore socket also receives
   * `notifications` events on the per-user channel. When it records one we
   * refetch — `handleCreated` dedupes by id, so double delivery via the main
   * Feathers socket is harmless.
   */
  private attachRealtimeFallback() {
    if (this.realtimeFallbackDisposer) return;
    const realtime = this.rootStore?.realtimeStore;
    if (!realtime) return;
    try {
      void realtime.connect();
    } catch {
      // Non-fatal — polling + the main socket still cover us.
    }
    this.realtimeFallbackDisposer = reaction(
      () => realtime.lastEvent,
      (event) => {
        if (event?.service === "notifications") {
          this.loadNotifications();
        }
      }
    );
  }

  private detachRealtimeFallback() {
    if (this.realtimeFallbackDisposer) {
      this.realtimeFallbackDisposer();
      this.realtimeFallbackDisposer = null;
    }
  }

  startPolling() {
    this.loadNotifications();
    this.attachListeners();
    this.attachRealtimeFallback();
    if (this.pollInterval) return;
    this.pollInterval = setInterval(() => this.loadNotifications(), 60000);
  }

  stopPolling() {
    // Invalidate any in-flight load so it cannot repopulate after sign-out.
    this.loadSeq++;
    this.detachListeners();
    this.detachRealtimeFallback();
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    runInAction(() => {
      this.notifications = [];
      this.unreadCount = 0;
    });
  }

  /**
   * Called by RootStore.purgeUserData on sign-out: the bell's rows and count
   * go, and a load still in flight for the person who left is discarded.
   * Polling and the socket listeners are not touched — the signed-in reaction
   * in the constructor owns them, and stops them as the session ends.
   */
  reset() {
    this.loadSeq++;
    this.notifications = [];
    this.unreadCount = 0;
    this.loading = false;
  }
}

export default ApiNotificationStore;
